import type { ReactNode } from "react";
import { createContext, useMemo, useState } from "react";

import { useIsomorphicLayoutEffect } from "../hooks/use-isomorphic-layout-effect";

export interface WindowSize {
  width: number;
  height: number;
}

export interface SizeConsumerProps {
  size: WindowSize | null;
}

export const SizeContext = createContext<SizeConsumerProps>({
  size: null,
});

export interface SizeProps {
  children?: ReactNode;
}

export function Size({ children }: SizeProps) {
  const [size, setSize] = useState<WindowSize | null>(
    typeof window !== "undefined"
      ? {
          width: window.innerWidth,
          height: window.innerHeight,
        }
      : null,
  );

  useIsomorphicLayoutEffect(() => {
    function onResize() {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", onResize);

    onResize();

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const context = useMemo(() => ({ size }), [size]);

  return (
    <SizeContext.Provider value={context}>{children}</SizeContext.Provider>
  );
}
